import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import 'react-datepicker/dist/react-datepicker.css';
import '../../Offers/Offers.scss';  // Brings in .dp-header + .modern-datepicker-calendar styling
import Av2Select from './Av2Select';

export type PresetKey = '7d' | '30d' | '90d' | '12m' | 'custom';
export type CompareMode = 'previous' | 'last_year' | 'custom' | 'off';

interface Props {
    preset: PresetKey;
    customFrom: Date | null;
    customTo: Date | null;
    compareMode: CompareMode;
    compareCustomFrom: Date | null;
    compareCustomTo: Date | null;
    onPresetChange: (next: PresetKey) => void;
    onCustomDatesChange: (from: Date | null, to: Date | null) => void;
    onCompareModeChange: (next: CompareMode) => void;
    onCompareCustomDatesChange: (from: Date | null, to: Date | null) => void;
}

const PRESETS: { key: Exclude<PresetKey, 'custom'>; labelId: string; defaultLabel: string }[] = [
    { key: '7d',  labelId: 'analyticsV2.range.last7',          defaultLabel: '7 days' },
    { key: '30d', labelId: 'analyticsV2.range.last30',         defaultLabel: '30 days' },
    { key: '90d', labelId: 'analyticsV2.range.last90',         defaultLabel: '90 days' },
    { key: '12m', labelId: 'analyticsV2.range.last12months',   defaultLabel: '12 months' },
];

/**
 * Same calendar header used by ProductionBoard + Offers: clicking the
 * month/year label opens a 3-column month grid drill-down. Kept local
 * because there's no shared CalendarHeader component in the codebase
 * (the pattern is duplicated across the few callers).
 */
const CalendarHeader: React.FC<any> = ({
    date, changeYear, changeMonth, decreaseMonth, increaseMonth,
    prevMonthButtonDisabled, nextMonthButtonDisabled,
}) => {
    const intl = useIntl();
    const [showMonths, setShowMonths] = useState(false);
    const headerRef = useRef<HTMLDivElement>(null);
    const year = date.getFullYear();
    const month = date.getMonth();

    const months = useMemo(() => {
        const fmt = new Intl.DateTimeFormat(intl.locale, { month: 'long' });
        return Array.from({ length: 12 }, (_, i) => {
            const name = fmt.format(new Date(2000, i, 1));
            return name.charAt(0).toUpperCase() + name.slice(1);
        });
    }, [intl.locale]);

    useEffect(() => {
        const cal = headerRef.current?.closest('.react-datepicker') as HTMLElement | null;
        if (showMonths) {
            cal?.classList.add('month-picker-active');
        } else {
            cal?.classList.remove('month-picker-active');
            const container = cal?.querySelector('.react-datepicker__month-container') as HTMLElement | null;
            if (container) container.style.minHeight = '';
        }
    }, [showMonths]);

    const openMonthPicker = () => {
        const cal = headerRef.current?.closest('.react-datepicker') as HTMLElement | null;
        const container = cal?.querySelector('.react-datepicker__month-container') as HTMLElement | null;
        if (container) container.style.minHeight = container.offsetHeight + 'px';
        setShowMonths(true);
    };

    if (showMonths) {
        return (
            <div ref={headerRef} className="dp-header dp-header--months">
                <div className="dp-year-nav">
                    <button type="button" className="dp-nav-btn" onClick={() => changeYear(year - 1)}>&#8249;</button>
                    <span className="dp-year-label">{year}</span>
                    <button type="button" className="dp-nav-btn" onClick={() => changeYear(year + 1)}>&#8250;</button>
                </div>
                <div className="dp-month-grid">
                    {months.map((m, i) => (
                        <button
                            type="button"
                            key={m}
                            className={`dp-month-btn${month === i ? ' active' : ''}`}
                            onClick={() => { changeMonth(i); setShowMonths(false); }}
                        >
                            {m}
                        </button>
                    ))}
                </div>
            </div>
        );
    }
    return (
        <div ref={headerRef} className="dp-header">
            <button type="button" className="dp-nav-btn" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>&#8249;</button>
            <button type="button" className="dp-month-year-btn" onClick={openMonthPicker}>
                {months[month]} {year} <span className="dp-chevron">&#9662;</span>
            </button>
            <button type="button" className="dp-nav-btn" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>&#8250;</button>
        </div>
    );
};

const DateRangeControl: React.FC<Props> = ({
    preset,
    customFrom,
    customTo,
    compareMode,
    compareCustomFrom,
    compareCustomTo,
    onPresetChange,
    onCustomDatesChange,
    onCompareModeChange,
    onCompareCustomDatesChange,
}) => {
    const intl = useIntl();
    const fm = (id: string, defaultMessage: string) =>
        intl.formatMessage({ id, defaultMessage });

    const weekdayNarrow = useCallback((d: string) => {
        const idx = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].indexOf(d);
        if (idx < 0) return d;
        const date = new Date(2024, 0, 7 + idx);
        return new Intl.DateTimeFormat(intl.locale, { weekday: 'narrow' }).format(date);
    }, [intl.locale]);

    // Use FormattedMessage JSX as the label rather than fm() string output:
    // pre-computing strings in a useMemo([intl.locale]) leaves the labels
    // frozen on whatever messages were loaded at first render. If the
    // translations finish loading later (common with the per-locale lazy
    // module load) the locale string stays 'sq', the memo never re-runs,
    // and the dropdown shows the English defaults. FormattedMessage
    // subscribes to intl internally so it always reflects current messages.
    const compareOptions = useMemo(
        () => [
            {
                value: 'previous',
                label: <FormattedMessage id="analyticsV2.compare.previous" defaultMessage="Previous period" />,
            },
            {
                value: 'last_year',
                label: <FormattedMessage id="analyticsV2.compare.lastYear" defaultMessage="Same period last year" />,
            },
            {
                value: 'custom',
                label: <FormattedMessage id="analyticsV2.compare.custom" defaultMessage="Specific period" />,
            },
            {
                value: 'off',
                label: <FormattedMessage id="analyticsV2.compare.off" defaultMessage="No comparison" />,
            },
        ],
        [],
    );

    return (
        <div className="av2-controls">
            <div className="av2-controls__group">
                <span className="av2-controls__label">
                    <FormattedMessage id="analyticsV2.range.label" defaultMessage="Range" />
                </span>
                <div className="av2-presets" role="group">
                    {PRESETS.map((p) => (
                        <button
                            key={p.key}
                            type="button"
                            className={`av2-preset ${preset === p.key ? 'av2-preset--active' : ''}`}
                            onClick={() => onPresetChange(p.key)}
                        >
                            <FormattedMessage id={p.labelId} defaultMessage={p.defaultLabel} />
                        </button>
                    ))}
                    <button
                        type="button"
                        className={`av2-preset ${preset === 'custom' ? 'av2-preset--active' : ''}`}
                        onClick={() => onPresetChange('custom')}
                    >
                        <FormattedMessage id="analyticsV2.range.custom" defaultMessage="Custom" />
                    </button>
                </div>

                {preset === 'custom' && (
                    <div className="av2-custom-dates">
                        <DatePicker
                            selected={customFrom}
                            onChange={(date: Date | null) => {
                                if (date && customTo && date.getTime() > customTo.getTime()) {
                                    onCustomDatesChange(date, date);
                                } else {
                                    onCustomDatesChange(date, customTo);
                                }
                            }}
                            maxDate={customTo ?? undefined}
                            dateFormat="dd/MM/yyyy"
                            placeholderText={fm('analyticsV2.range.from', 'From')}
                            className="av2-date-input"
                            calendarClassName="modern-datepicker-calendar"
                            popperPlacement="bottom-start"
                            popperContainer={({ children }: any) =>
                                ReactDOM.createPortal(children, document.body)
                            }
                            formatWeekDay={weekdayNarrow}
                            renderCustomHeader={(p: any) => <CalendarHeader {...p} />}
                            isClearable
                        />
                        <span className="av2-custom-dates__sep">–</span>
                        <DatePicker
                            selected={customTo}
                            onChange={(date: Date | null) => onCustomDatesChange(customFrom, date)}
                            minDate={customFrom ?? undefined}
                            dateFormat="dd/MM/yyyy"
                            placeholderText={fm('analyticsV2.range.to', 'To')}
                            className="av2-date-input"
                            calendarClassName="modern-datepicker-calendar"
                            popperPlacement="bottom-start"
                            popperContainer={({ children }: any) =>
                                ReactDOM.createPortal(children, document.body)
                            }
                            formatWeekDay={weekdayNarrow}
                            renderCustomHeader={(p: any) => <CalendarHeader {...p} />}
                            isClearable
                        />
                    </div>
                )}
            </div>

            <div className="av2-controls__group av2-controls__group--right">
                <span className="av2-controls__label">
                    <FormattedMessage id="analyticsV2.compare.label" defaultMessage="Compare" />
                </span>
                <Av2Select
                    options={compareOptions}
                    value={compareMode}
                    onChange={(v) => onCompareModeChange(v as CompareMode)}
                    ariaLabel={fm('analyticsV2.compare.label', 'Compare')}
                />

                {compareMode === 'custom' && (
                    <div className="av2-custom-dates">
                        <DatePicker
                            selected={compareCustomFrom}
                            onChange={(date: Date | null) => {
                                if (date && compareCustomTo && date.getTime() > compareCustomTo.getTime()) {
                                    onCompareCustomDatesChange(date, date);
                                } else {
                                    onCompareCustomDatesChange(date, compareCustomTo);
                                }
                            }}
                            maxDate={compareCustomTo ?? undefined}
                            dateFormat="dd/MM/yyyy"
                            placeholderText={fm('analyticsV2.range.from', 'From')}
                            className="av2-date-input"
                            calendarClassName="modern-datepicker-calendar"
                            popperPlacement="bottom-end"
                            popperContainer={({ children }: any) =>
                                ReactDOM.createPortal(children, document.body)
                            }
                            formatWeekDay={weekdayNarrow}
                            renderCustomHeader={(p: any) => <CalendarHeader {...p} />}
                            isClearable
                        />
                        <span className="av2-custom-dates__sep">–</span>
                        <DatePicker
                            selected={compareCustomTo}
                            onChange={(date: Date | null) =>
                                onCompareCustomDatesChange(compareCustomFrom, date)
                            }
                            minDate={compareCustomFrom ?? undefined}
                            dateFormat="dd/MM/yyyy"
                            placeholderText={fm('analyticsV2.range.to', 'To')}
                            className="av2-date-input"
                            calendarClassName="modern-datepicker-calendar"
                            popperPlacement="bottom-end"
                            popperContainer={({ children }: any) =>
                                ReactDOM.createPortal(children, document.body)
                            }
                            formatWeekDay={weekdayNarrow}
                            renderCustomHeader={(p: any) => <CalendarHeader {...p} />}
                            isClearable
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default DateRangeControl;
